import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import GradientWrapper from '@/common/GradientWrapper'

import { useBecomeValidatorTx } from './hooks/become-validator-tx'

export const descriptionSchema = z.object({
  validator_pubkey: z
    .string()
    .min(1, 'Validator pubkey is required')
    .regex(/^[A-Za-z0-9+/=]+$/, 'Must be a base64 string')
    .refine(value => {
      try {
        const decoded = atob(value)
        return decoded.length === 32
      } catch {
        return false
      }
    }, 'Pubkey must decode to 32 bytes'),
  moniker: z
    .string()
    .min(1, 'Validator name is required')
    .max(50, 'Maximum 50 characters'),
  identity: z.string().optional().or(z.literal('')),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  security_contact: z
    .string()
    .email('Invalid email')
    .optional()
    .or(z.literal('')),
  details: z
    .string()
    .max(200, 'Maximum 200 characters')
    .optional()
    .or(z.literal('')),
})

export type DescriptionValues = z.infer<typeof descriptionSchema>

export function BecomeValidatorDetailsStep({
  onSuccess,
  onSubmittingChange,
  selectedAddresses,
  amountAtomic,
}: {
  onSuccess: () => void
  onSubmittingChange: (isSubmitting: boolean) => void
  selectedAddresses?: string[]
  amountAtomic?: string
}) {
  const { submitBecomeValidator } = useBecomeValidatorTx(onSuccess)

  const form = useForm<DescriptionValues>({
    resolver: zodResolver(descriptionSchema),
    mode: 'onBlur',
    defaultValues: {
      validator_pubkey: '',
      moniker: '',
      identity: '',
      website: '',
      security_contact: '',
      details: '',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form

  useEffect(() => {
    onSubmittingChange(isSubmitting)
  }, [isSubmitting, onSubmittingChange])

  const submit = async (values: DescriptionValues) => {
    await submitBecomeValidator({
      validatorPubkeyBase64: values.validator_pubkey,
      moniker: values.moniker,
      identity: values.identity,
      website: values.website,
      securityContact: values.security_contact,
      details: values.details,
      payload:
        amountAtomic && amountAtomic.length > 0
          ? { type: 'amount', amountAtomic }
          : { type: 'nfts', nftAddresses: selectedAddresses || [] },
    })
  }

  return (
    <form
      id='become-validator-form'
      onSubmit={handleSubmit(submit)}
      className='flex flex-col gap-4'
    >
      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>
          Validator pubkey (base64)
        </span>
        <GradientWrapper>
          <input
            {...register('validator_pubkey')}
            placeholder='ed25519, 32 bytes (e.g. 4W0jzdx7...)'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
        {errors.validator_pubkey && (
          <span className='text-xs text-red-400'>
            {errors.validator_pubkey.message}
          </span>
        )}
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>Validator name</span>
        <GradientWrapper>
          <input
            {...register('moniker')}
            placeholder='Your validator name'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
        {errors.moniker && (
          <span className='text-xs text-red-400'>{errors.moniker.message}</span>
        )}
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>
          Identity (optional)
        </span>
        <GradientWrapper>
          <input
            {...register('identity')}
            placeholder='Keybase / UPort ID'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>
          Website (optional)
        </span>
        <GradientWrapper>
          <input
            {...register('website')}
            placeholder='https://example.com'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
        {errors.website && (
          <span className='text-xs text-red-400'>{errors.website.message}</span>
        )}
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>
          Security email (optional)
        </span>
        <GradientWrapper>
          <input
            {...register('security_contact')}
            placeholder='security@example.com'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
        {errors.security_contact && (
          <span className='text-xs text-red-400'>
            {errors.security_contact.message}
          </span>
        )}
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-muted-foreground text-xs'>
          Details (optional)
        </span>
        <GradientWrapper className='h-[100px]'>
          <textarea
            {...register('details')}
            placeholder='Short validator description'
            disabled={isSubmitting}
            className='bg-input text-foreground border-border h-[100px] w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
        </GradientWrapper>
      </div>
    </form>
  )
}
